import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import { getComments, createCommentData, updateCommentData, archiveCommentData } from '../Api/Comment/commentApi';
import { User } from './User';

export type Comment = {
  id?: string;
  entityId: string;
  entityType: string;
  parent_comment_id?: Comment | string;
  commentText: string;
  createdBy?: User;
  archivedBy?: User;
  archived?: boolean;
  textChangedAt?: string;
  createdAt?: string;
};

export type ErrorType = { message: string } | null;

const commentsMap = new Map<string, BehaviorSubject<Comment[]>>();
const commentsLoadingMap = new Map<string, BehaviorSubject<boolean>>();

export const getCommentSubject = (entityId: string, entityType: string): BehaviorSubject<Comment[]> => {
  const key = `${entityId}-${entityType}`;
  let subject = commentsMap.get(key);
  if (!subject) {
    subject = new BehaviorSubject<Comment[]>([]);
    commentsMap.set(key, subject);
  }
  return subject;
};

export const getCommentLoadingSubject = (entityId: string, entityType: string): BehaviorSubject<boolean> => {
  const key = `${entityId}-${entityType}`;
  let subject = commentsLoadingMap.get(key);
  if (!subject) {
    subject = new BehaviorSubject<boolean>(false);
    commentsLoadingMap.set(key, subject);
  }
  return subject;
};

export const fetchComments = async (entityId: string, entityType: string) => {
  const loadingSubject = getCommentLoadingSubject(entityId, entityType);
  const commentsSubject = getCommentSubject(entityId, entityType);

  if (loadingSubject.getValue()) return;
  loadingSubject.next(true);

  const { data, error } = await getComments(entityId, entityType);

  if (data) {
    clearError('comment');
    const patchedData = data
      .map((comment: Comment) => {
        comment.parent_comment_id =
          typeof comment.parent_comment_id === 'object' ? comment.parent_comment_id?.id : comment.parent_comment_id;
        return comment;
      })
      .reverse();
    commentsSubject.next(patchedData);
  }
  if (error) addError('comment', error.message);

  loadingSubject.next(false);
};

export const createComment = async (comment: Comment) => {
  const commentsSubject = getCommentSubject(comment.entityId, comment.entityType);
  const { data, error } = await createCommentData(comment);
  if (data) {
    clearError('comment');
    const currentState = [...commentsSubject.getValue()];
    commentsSubject.next([data, ...currentState]);
  }

  if (error) addError('comment', error.message);
};

export const refreshComment = (entityId: string, entityType: string, data: Comment | null, error: ErrorType) => {
  const commentsSubject = getCommentSubject(entityId, entityType);

  if (data) {
    clearError('comment');
    const currentState = [...commentsSubject.getValue()];
    const updatedComments = currentState.map((com: Comment) => (com.id === data.id ? data : com));
    commentsSubject.next(updatedComments);
  }

  if (error) addError('comment', error.message);
};

export const updateComment = async (comment: Comment) => {
  const { data, error } = await updateCommentData(comment);

  refreshComment(comment.entityId, comment.entityType, data, error);
};

export const archiveComment = async (comment: Comment) => {
  if (!comment.id) return;

  const { error } = await archiveCommentData(comment.id);
  const archivedComment: Comment = {
    ...comment,
    commentText: 'This comment was deleted',
    archived: true
  };
  refreshComment(comment.entityId, comment.entityType, archivedComment, error);
};
